// 🔧 event_context_provider.js
// ✅ Purpose: Shared event context provider for all event-scoped routes
// ✅ Features:
// - Resolves :eventslug only once per event route
// - Loads events/{eventId} only once
// - Shares eventId, eventDoc, eventLoading, and ready with all child components
// - Reduces duplicate Firestore reads from RequireValidEvent, RoleGuard, and pages
// - Prevents mobile cold-load spinner delays caused by repeated event lookups
// - Keeps existing useEventContext() API compatible
// - Fully block-commented for clarity and future devs

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

import { useLocation, useParams } from "react-router-dom";
import { db } from "../firebase";
import { logger } from "../utils/logger";

// ✅ Internal React Context used by the provider and hook.
// ✅ Exported only through helper functions below.
const EventContext = createContext(null);

// ✅ Safe helper so hooks can check whether they are inside the provider.
export const useEventContextValue = () => {
  return useContext(EventContext);
};

export const EventContextProvider = ({ children }) => {
  const { eventslug } = useParams();
  const location = useLocation();

  const [eventId, setEventId] = useState(null);
  const [eventDoc, setEventDoc] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadEventContext = async () => {
      // ✅ Every route change starts with a clean event state.
      setEventId(null);
      setEventDoc(null);
      setEventError(null);

      // ✅ If this provider ever gets mounted without an eventslug,
      // ✅ do not trap the app in a loading state.
      if (!eventslug) {
        logger.log("ℹ️ EventContextProvider mounted without eventslug.", {
          pathname: location.pathname,
        });

        setEventLoading(false);
        return;
      }

      setEventLoading(true);

      logger.log("🔄 EventContextProvider resolving event...", {
        eventslug,
        pathname: location.pathname,
      });

      try {
        // ✅ Step 1: Resolve the event document ID from the public slug.
        const eventQuery = query(
          collection(db, "events"),
          where("slug", "==", eventslug),
          limit(1)
        );

        const eventSnap = await getDocs(eventQuery);

        if (cancelled) return;

        if (eventSnap.empty) {
          logger.warn("⚠️ EventContextProvider could not find event slug.", {
            eventslug,
          });

          setEventError("event-not-found");
          return;
        }

        const resolvedEventId = eventSnap.docs[0].id;

        // ✅ Step 2: Load the full event document once.
        const eventRef = doc(db, "events", resolvedEventId);
        const fullEventSnap = await getDoc(eventRef);

        if (cancelled) return;

        if (!fullEventSnap.exists()) {
          logger.warn("⚠️ EventContextProvider event doc missing.", {
            eventId: resolvedEventId,
          });

          setEventError("event-doc-not-found");
          return;
        }

        const loadedEventDoc = {
          id: fullEventSnap.id,
          ...fullEventSnap.data(),
        };

        setEventId(resolvedEventId);
        setEventDoc(loadedEventDoc);

        logger.log("✅ EventContextProvider ready.", {
          eventslug,
          eventId: resolvedEventId,
        });
      } catch (err) {
        if (cancelled) return;

        logger.error("❌ EventContextProvider failed.", {
          value: err,
        });

        setEventError("event-context-error");
      } finally {
        if (!cancelled) {
          setEventLoading(false);
        }
      }
    };

    loadEventContext();

    return () => {
      cancelled = true;
    };
  }, [eventslug, location.pathname]);

  const value = useMemo(
    () => ({
      eventslug,
      eventId,
      eventDoc,
      eventLoading,
      eventError,
      ready: !!eventId && !!eventDoc && !eventLoading && !eventError,
    }),
    [eventslug, eventId, eventDoc, eventLoading, eventError]
  );

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};

export default EventContextProvider;